import React, { useState } from "react";
import { fetchJson } from "../../lib/api";
import { channels } from "../../lib/constants";
import { useI18n } from "../../i18n/i18n";
import { Button } from "../button";
import { Card, CardContent, CardHeader, CardTitle } from "../card";
import { Input } from "../input";

export const ChannelConnectModal = ({
  channel,
  houseId,
  onClose,
  onConnected,
}: {
  channel: (typeof channels)[number];
  houseId: number;
  onClose: () => void;
  onConnected: (channelId: (typeof channels)[number]["id"]) => void;
}) => {
  const { t } = useI18n();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [refreshToken, setRefreshToken] = useState("");
  const [meteringPoints, setMeteringPoints] = useState<Array<{ id: string; label: string }>>([]);
  const [selectedMeteringPointId, setSelectedMeteringPointId] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isEloverblik = channel.id === "eloverblik";
  const hasMeteringPoints = meteringPoints.length > 0;

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);
    setIsSubmitting(true);
    try {
      if (isEloverblik) {
        const response = await fetchJson<{
          status: "select_metering_point" | "saved";
          meteringPoints?: Array<{ id: string; label: string }>;
        }>("/api/houses/" + houseId + "/credentials", {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({
            channel: channel.id,
            refreshToken,
            meteringPointId: selectedMeteringPointId || undefined,
          }),
        });
        if (response.status === "select_metering_point") {
          const options = response.meteringPoints ?? [];
          setMeteringPoints(options);
          const first = options[0]?.id ?? "";
          setSelectedMeteringPointId((current) => current || first);
          return;
        }
      } else {
        await fetchJson("/api/houses/" + houseId + "/credentials", {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({ channel: channel.id, email, password }),
        });
      }
      onConnected(channel.id);
    } catch (submitError) {
      setError(
        submitError instanceof Error ? submitError.message : t("channels.saveFailed", "Failed to save"),
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 p-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle>
            {t("channels.connectTitle", "Connect {channel}", {
              channel: t(`channel.label.${channel.id}`, channel.label),
            })}
          </CardTitle>
          <p className="text-sm text-muted">
            {t(`channel.description.${channel.id}`, channel.description)}
          </p>
        </CardHeader>
        <CardContent className="space-y-3">
          <form className="space-y-2" onSubmit={handleSubmit}>
            {isEloverblik ? (
              <>
                <p className="text-xs text-muted">
                  {t(
                    "channels.eloverblikTokenHelp",
                    "Get your refresh token from Step 1 in the ElOverblik Customer guide, then paste it here.",
                  )}{" "}
                  <a
                    href="https://docs.eloverblik.dk/docs/guides/api/customer"
                    target="_blank"
                    rel="noopener"
                    className="underline"
                  >
                    {t("channels.eloverblikGuide", "Open guide")}
                  </a>
                </p>
                <Input
                  value={refreshToken}
                  onChange={(event) => setRefreshToken(event.target.value)}
                  placeholder={t("channels.eloverblikRefreshToken", "Refresh token")}
                  type="password"
                  required
                />
                {hasMeteringPoints ? (
                  <select
                    className="h-9 w-full rounded-md border px-3 text-sm brand-input brand-focus"
                    value={selectedMeteringPointId}
                    onChange={(event) => setSelectedMeteringPointId(event.target.value)}
                  >
                    {meteringPoints.map((point) => (
                      <option key={point.id} value={point.id}>
                        {point.label}
                      </option>
                    ))}
                  </select>
                ) : null}
              </>
            ) : (
              <>
                <Input
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  placeholder={t("auth.email", "Email")}
                  type="email"
                />
                <Input
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  placeholder={t("auth.password", "Password")}
                  type="password"
                />
              </>
            )}
            {error ? <p className="text-sm text-error">{error}</p> : null}
            <div className="flex justify-end gap-2">
              <Button type="button" variant="ghost" onClick={onClose}>
                {t("common.cancel", "Cancel")}
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting
                  ? t("channels.verifying", "Verifying...")
                  : isEloverblik && hasMeteringPoints
                    ? t("channels.eloverblikSaveSelection", "Save selection")
                    : isEloverblik
                      ? t("channels.eloverblikFindMeteringPoints", "Find houses")
                      : t("channels.connect", "Connect")}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};
